<div class="sk-dialog">

  <!-- ── Header ─────────────────────────────────────── -->
  <header class="sk-dialog__header">
    <div class="sk-dialog__header-icon">
      <mat-icon>account_circle</mat-icon>
    </div>
    <div class="sk-dialog__header-text">
      <span class="sk-dialog__header-title">
        {{ dynamicDataService.getTitleByFieldName('lblMyProfilePageTitle', pageDynamicData) | translate }}
      </span>
      <span class="sk-dialog__header-sub">
        {{ 'Update your personal details and profile picture' | translate }}
      </span>
    </div>
    <button class="sk-dialog__header-close" (click)="dialogRef.close({ refreshPage: false })" [attr.aria-label]="'Close' | translate">
      <mat-icon>close</mat-icon>
    </button>
  </header>

  <!-- ── Body ───────────────────────────────────────── -->
  <div class="sk-dialog__body">
    <form [formGroup]="userFormGroup">

      <div class="sk-dialog__section">
        <div class="sk-dialog__section-label">
          <span class="sk-dialog__section-num">01</span>
          <span class="sk-dialog__section-title">{{ 'Profile Details' | translate }}</span>
        </div>

        <div class="sk-profile-layout">

          <!-- Avatar column -->
          <div class="sk-profile-avatar">
            <div class="image-upload">
              <div class="cld-image-cropper-ctrl sk-image-cropper-ctrl" *ngIf="validFileUploaded">
                <image-cropper [imageChangedEvent]="imageChangedEvent" [maintainAspectRatio]="true"
                  [aspectRatio]="1 / 1" format="png" [output]="'base64'" (imageCropped)="imageCropped($event)"
                  [cropperMinWidth]="32" [cropperMinHeight]="32" [resizeToWidth]="98" [resizeToHeight]="98"
                  [roundCropper]="true">
                </image-cropper>
              </div>

              <label for="imageInput">
                <div *ngIf="!validFileUploaded" class="sk-avatar-center avatar-center icon-avatar">
                  <div *ngIf="!userProfile" class="sk-avatar-initials">{{ userInitials }}</div>
                  <img *ngIf="userProfile" [src]="userProfile" class="sk-avatar-img" [alt]="'Profile picture' | translate" />
                  <div class="hover-icon">
                    <mat-icon>photo_camera</mat-icon>
                  </div>
                </div>
              </label>

              <span class="sk-remove-picture-lnk" (click)="removePicture()">
                {{ dynamicDataService.getTitleByFieldName('lblRemovePicture', pageDynamicData) | translate }}
              </span>
              <span *ngIf="pictureRemoved && !validFileUploaded && userData?.UserProfilePic" class="sk-remove-picture-hint" role="status">
                {{ 'Click Save to remove the picture' | translate }}
              </span>

              <input id="imageInput" #imageInput accept="image/png , image/jpeg" type="file" hidden class="hidden-file-input"
                (change)="fileChangeEvent($event)" />
            </div>
          </div>

          <!-- Fields column -->
          <div class="sk-profile-fields">

            <div class="sk-dialog__grid">
              <div class="sk-dialog__field">
                <label class="sk-dialog__label sk-dialog__label--required">
                  {{ dynamicDataService.getTitleByFieldName('lblName', pageDynamicData) | translate }}
                </label>
                <mat-form-field appearance="outline">
                  <input type="text" formControlName="userName" maxlength="25"
                    [placeholder]="dynamicDataService.getTitleByFieldName('lblName', pageDynamicData) | translate" matInput
                    (change)="userFormGroup.controls['userName'].setValue(userFormGroup.controls['userName'].value.toString().trim())" />
                  <mat-error *ngIf="userFormControls['userName'].errors?.['required']">
                    {{ 'Required field' | translate }}
                  </mat-error>
                </mat-form-field>
              </div>

              <div class="sk-dialog__field">
                <label class="sk-dialog__label sk-dialog__label--required">
                  {{ dynamicDataService.getTitleByFieldName('lblDesignation', pageDynamicData) | translate }}
                </label>
                <mat-form-field appearance="outline">
                  <input type="text" formControlName="userDesignation" maxlength="25"
                    [placeholder]="dynamicDataService.getTitleByFieldName('lblDesignation', pageDynamicData) | translate" matInput
                    (change)="userFormGroup.controls['userDesignation'].setValue(userFormGroup.controls['userDesignation'].value.toString().trim())" />
                  <mat-error *ngIf="userFormControls['userDesignation'].errors?.['required']">
                    {{ 'Required field' | translate }}
                  </mat-error>
                </mat-form-field>
              </div>
            </div>

            <div class="sk-dialog__grid sk-dialog__grid--single">
              <div class="sk-dialog__field">
                <label class="sk-dialog__label">
                  {{ dynamicDataService.getTitleByFieldName('lblEmail', pageDynamicData) | translate }}
                </label>
                <mat-form-field appearance="outline">
                  <input type="text" formControlName="userEmail"
                    [placeholder]="'Enter work email' | translate"
                    [disabled]="true" matInput />
                </mat-form-field>
              </div>
            </div>

            <!-- Hierarchy (read-only): the user's place in the RBAC role-rank / escalation
                 ladder, shown as capsules. Derived from the RBAC module, not editable here. -->
            <div class="sk-dialog__grid sk-dialog__grid--single">
              <div class="sk-dialog__field">
                <label class="sk-dialog__label">{{ 'Hierarchy' | translate }}</label>
                <div class="sk-hierarchy" role="group" [attr.aria-label]="'Hierarchy' | translate">
                  <span class="sk-hierarchy__muted" *ngIf="hierarchyLoading">{{ 'Loading' | translate }}…</span>
                  <ng-container *ngIf="!hierarchyLoading">
                    <ng-container *ngIf="hierarchyRoles.length; else noRole">
                      <span class="sk-hierarchy__chip sk-hierarchy__chip--role" *ngFor="let role of hierarchyRoles">
                        <mat-icon aria-hidden="true">badge</mat-icon>{{ role }}
                      </span>
                      <ng-container *ngIf="hierarchyEscalatesTo">
                        <mat-icon class="sk-hierarchy__arrow" aria-hidden="true">arrow_upward</mat-icon>
                        <span class="sk-hierarchy__chip sk-hierarchy__chip--escalate">
                          {{ 'Escalates to' | translate }}: {{ hierarchyEscalatesTo }}
                        </span>
                      </ng-container>
                      <span class="sk-hierarchy__chip sk-hierarchy__chip--top"
                            *ngIf="hierarchyRank !== null && !hierarchyEscalatesTo">
                        <mat-icon aria-hidden="true">workspace_premium</mat-icon>{{ 'Top of approval chain' | translate }}
                      </span>
                    </ng-container>
                    <ng-template #noRole>
                      <span class="sk-hierarchy__muted">{{ 'No role assigned' | translate }}</span>
                    </ng-template>
                  </ng-container>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>

    </form>
  </div>

  <!-- ── Footer ─────────────────────────────────────── -->
  <footer class="sk-dialog__footer">
    <button class="sk-dialog__btn sk-dialog__btn--ghost" (click)="dialogRef.close({ refreshPage: false })">
      {{ dynamicDataService.getTitleByFieldName('btnCancel', pageDynamicData) | translate }}
    </button>
    <button class="sk-dialog__btn sk-dialog__btn--primary" (click)="saveProfile()">
      <mat-icon>save</mat-icon>
      {{ dynamicDataService.getTitleByFieldName('btnSave', pageDynamicData) | translate }}
    </button>
  </footer>

</div>
